import React, {useRef, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import YouTube from 'react-youtube'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PlayIcon} from '../icons'
import {remCalc} from '../../theme/_utils'
import LoadingSpinner from '../loading-spinner'
import {BRAND_KEY} from '../../brand'

/**
 * Extracts the YouTube video ID from a given URL.
 *
 * @param {string} url - The URL of the YouTube video.
 * @returns {string|null} The YouTube video ID if found, otherwise null.
 */
const getYoutubeVideoId = (url) => {
    // If the input is the ID itself, return the input
    if (typeof url === 'string' && url.length === 11) {
        return url
    }

    var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\\&v=)([^#\\&\\?]*).*/
    var match = url?.match(regExp)
    if (match && match[2].length == 11) {
        return match[2]
    } else {
        return null
    }
}

/**
 * A React component that renders a YouTube iframe.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the video.
 * @param {string} props.videoSrc - The source URL of the YouTube video.
 * @param {string|Object} [props.height] - The height of the iframe.
 * @returns {JSX.Element} The rendered YouTube iframe component.
 */
const YoutubeIframe = ({
    title,
    videoSrc,
    thumbnail = null,
    onPause,
    onPlay,
    isVariantStyle,
    enableInteractionHandlers = false,
}) => {
    const YoutubeVideoId = getYoutubeVideoId(videoSrc)
    const ref = useRef()
    const [dimensions, setDimensions] = useState({loaded: false})
    const [showVideo, setShowVideo] = useState(thumbnail ? false : true)

    const isKenwood = BRAND_KEY === 'kenwood'

    React.useEffect(() => {
        let {height} = ref.current.getBoundingClientRect()
        setDimensions({height, loaded: true})
    }, [])

    const opts = {
        height: dimensions.height,
        width: '100%',
        playerVars: {
            autoplay: thumbnail ? 1 : 0,
        },
    }

    const handleStateChange = (event) => {
        if (enableInteractionHandlers && event.data === 1 && onPlay && isVariantStyle) {
            onPlay()
        }
        if (enableInteractionHandlers && event.data === 2 && onPause) {
            onPause()
        }
    }

    useEffect(() => {
        function updateHeight() {
            if (ref.current) {
                let {height} = ref.current.getBoundingClientRect()
                setDimensions({height, loaded: true})
            }
        }
        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [])

    useEffect(() => {
        if (!thumbnail) setShowVideo(true)
    }, [thumbnail])

    return (
        <Box
            ref={ref}
            w="100%"
            h={isVariantStyle ? {base: '100vw', md: '100%'} : '100%'}
            data-testid="video-container"
        >
            {!showVideo && thumbnail && (
                <Box
                    as="button"
                    onClick={() => setShowVideo(true)}
                    style={{
                        backgroundImage: `url(${thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    <PlayIcon
                        height={remCalc(40)}
                        width={remCalc(40)}
                        fill="background.default"
                        color={isKenwood ? 'background.alternative' : 'currentColor'}
                    />
                </Box>
            )}
            {!dimensions.loaded && !showVideo && (
                <LoadingSpinner data-testid="loading-spinner-video" />
            )}
            {dimensions.loaded && showVideo && (
                <YouTube
                    data-testid="youtube-video"
                    title={title}
                    videoId={YoutubeVideoId}
                    opts={opts}
                    onStateChange={handleStateChange}
                />
            )}
        </Box>
    )
}

YoutubeIframe.propTypes = {
    title: PropTypes.string,
    videoSrc: PropTypes.string,
    thumbnail: PropTypes.string,
    onBlur: PropTypes.func,
    onPause: PropTypes.func,
    onPlay: PropTypes.func,
    enableInteractionHandlers: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isVariantStyle: PropTypes.bool,
}

export default YoutubeIframe
